// Thin wrapper around the Google Identity Services (GSI) token
// flow. Returns an access token scoped to whatever the caller
// asks for; the token lives in memory only - never persisted to
// localStorage / cookies, never sent to InRange's backend. The
// caller passes it as an `Authorization: Bearer …` header on
// direct Gmail API calls.
//
// This is operator-side OAuth (the operator signs in to their own
// Google account to grant the v2 app read access to their Gmail);
// it is NOT the InRange API auth seam (which lives in `web/src/auth/`
// and is wired via Kinde).
import { GOOGLE_CLIENT_ID } from "./google-config";

// Minimal hand-rolled types for the bits of the GSI library we
// use. The npm `@types/google.accounts` package exists but is
// stale; ten lines of declare-global here is less moving-target.
declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (resp: {
              access_token?: string;
              scope?: string;
              error?: string;
              error_description?: string;
            }) => void;
            error_callback?: (err: { type?: string }) => void;
          }) => {
            requestAccessToken: (opts?: { prompt?: string }) => void;
          };
        };
      };
      // Picker SDK shape - only what we actually call. Real types
      // are in @types/google.picker but it's stale; matching what
      // we use is less moving-target.
      picker: GooglePicker;
    };
    gapi?: {
      load: (lib: string, cb: () => void) => void;
    };
  }
}

interface GooglePicker {
  PickerBuilder: new () => PickerBuilder;
  DocsView: new (viewId?: unknown) => PickerDocsView;
  ViewId: { DOCS: unknown; FOLDERS: unknown };
  Feature: { MULTISELECT_ENABLED: unknown };
  Action: { PICKED: string; CANCEL: string };
}

export interface PickerDoc {
  id: string;
  name: string;
  mimeType: string;
  // Other fields set by Picker (sizeBytes / iconUrl / etc.) are
  // ignored; the recursive listing pulls metadata fresh from
  // drive.files.list anyway.
}

interface PickerBuilder {
  enableFeature: (f: unknown) => PickerBuilder;
  setOAuthToken: (t: string) => PickerBuilder;
  setDeveloperKey: (k: string) => PickerBuilder;
  addView: (v: unknown) => PickerBuilder;
  setCallback: (cb: (data: { action: string; docs?: PickerDoc[] }) => void) => PickerBuilder;
  build: () => { setVisible: (v: boolean) => void };
}

interface PickerDocsView {
  setIncludeFolders: (v: boolean) => PickerDocsView;
  setSelectFolderEnabled: (v: boolean) => PickerDocsView;
  setMimeTypes: (s: string) => PickerDocsView;
}

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GAPI_SCRIPT_SRC = "https://apis.google.com/js/api.js";

const loadedScripts = new Set<string>();

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (loadedScripts.has(src) || document.querySelector(`script[src="${src}"]`)) {
      loadedScripts.add(src);
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = src;
    tag.async = true;
    tag.defer = true;
    tag.onload = () => {
      loadedScripts.add(src);
      resolve();
    };
    tag.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(tag);
  });

export const ensureGsiLoaded = (): Promise<void> => loadScript(GSI_SCRIPT_SRC);

// Load the gapi loader script + the Picker library on top of it.
// Two separate loads because the picker module is requested via
// `gapi.load("picker", cb)` after the loader itself is ready.
// Memoised separately from GSI so a Gmail-only session doesn't
// pay the picker load cost.
let pickerReadyPromise: Promise<void> | null = null;
export const ensureGapiPickerLoaded = (): Promise<void> => {
  if (pickerReadyPromise) return pickerReadyPromise;
  pickerReadyPromise = (async () => {
    await loadScript(GAPI_SCRIPT_SRC);
    if (!window.gapi) {
      throw new GoogleAuthError("Google API loader (gapi) failed to register.");
    }
    await new Promise<void>((resolve) => {
      window.gapi!.load("picker", () => resolve());
    });
  })().catch((e) => {
    // On failure clear the memoised promise so a retry can re-run.
    pickerReadyPromise = null;
    throw e;
  });
  return pickerReadyPromise;
};

export class GoogleAuthError extends Error {
  // Set to `true` when the error is a benign user-initiated cancel
  // (the operator closed the consent popup, or the popup never
  // opened due to a pop-up blocker). Callers should treat these as
  // "silent abort" - clear progress state but don't surface a red
  // error box. Distinct from a hard origin/scope mismatch which
  // genuinely needs the operator's attention.
  isUserCancelled = false;
}

// Request an OAuth access token for the given scopes. The
// `prompt: "consent"` flag forces a fresh consent dialog rather
// than reusing whatever token grant the user already has - matters
// when adding a new scope (Gmail) on top of a previously-granted
// Drive token, because the cached token wouldn't carry the new
// scope and the silent reuse would fail the scope-coverage check
// below.
//
// The caller (GmailSearchPanel) caches the returned token in
// React state for the page lifetime so repeated searches don't
// retrigger consent. When the token eventually expires (~1h) the
// caller catches the 401 and re-invokes this function to re-prompt.
export const requestGoogleAccessToken = (scopes: string[]): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(
        new GoogleAuthError(
          "Google Identity Services library not loaded - call ensureGsiLoaded() first.",
        ),
      );
      return;
    }
    const scopeStr = scopes.join(" ");
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: scopeStr,
      callback: (resp) => {
        if (resp.error || !resp.access_token) {
          // Origin-mismatch errors arrive here (not error_callback)
          // when the OAuth client's Authorized JavaScript origins
          // allowlist doesn't include the current page's origin.
          // GSI sets resp.error to "access_denied" and
          // resp.error_description to something like
          // "Error 400: redirect_uri_mismatch" or similar. The
          // legacy showcase showed the operator a useless raw
          // string; surface a friendly message naming John as the
          // fix contact instead.
          const errStr = resp.error_description ?? resp.error ?? "";
          if (
            /origin|redirect_uri|access[_ ]denied/i.test(errStr) ||
            resp.error === "access_denied"
          ) {
            reject(
              new GoogleAuthError(
                "This deploy isn't authorised for Google sign-in. The Google " +
                  "OAuth client's Authorized JavaScript origins allowlist " +
                  "needs to include this CloudFront origin. Tell John; this " +
                  "is a Google Cloud Console setting.",
              ),
            );
            return;
          }
          reject(
            new GoogleAuthError(
              resp.error_description ?? resp.error ?? "OAuth request failed",
            ),
          );
          return;
        }
        const granted = (resp.scope ?? "").split(/\s+/);
        const missing = scopes.filter((s) => !granted.includes(s));
        if (missing.length > 0) {
          reject(
            new GoogleAuthError(
              `OAuth grant returned scopes [${granted.join(", ")}], missing ${missing.join(", ")}. ` +
                "Revoke previous access at https://myaccount.google.com/permissions and try again.",
            ),
          );
          return;
        }
        resolve(resp.access_token);
      },
      error_callback: (err) => {
        // GSI's `error_callback` fires for OAuth flow setup
        // failures (popup couldn't open, popup closed, library
        // init failure). Origin-mismatch errors are NOT emitted
        // here despite what the legacy comment thread suggested -
        // they surface through the `callback` path with
        // resp.error set, handled above. Keep this branch to the
        // narrow set of types GSI actually emits.
        const t = err?.type ?? "";
        if (t === "popup_closed" || t === "popup_failed_to_open") {
          const e = new GoogleAuthError("Sign-in cancelled.");
          e.isUserCancelled = true;
          reject(e);
          return;
        }
        if (t === "idpiframe_initialization_failed") {
          reject(
            new GoogleAuthError(
              "Google sign-in couldn't initialise on this page. Often a " +
                "popup blocker or third-party-cookie restriction; try " +
                "again with the blocker disabled, or in an incognito " +
                "window with cookies allowed for google.com.",
            ),
          );
          return;
        }
        reject(new GoogleAuthError(t || "OAuth error"));
      },
    });
    tokenClient.requestAccessToken({ prompt: "consent" });
  });
};

// Module-scoped dedup so that when six concurrent workers all
// 401 simultaneously (Drive picker) they collapse onto a single
// consent popup. Keyed on the sorted scope string so a Gmail-only
// refresh doesn't reuse a Drive token grant (which would carry the
// wrong scopes and fail the scope-coverage check inside
// `requestGoogleAccessToken`).
//
// The promise is cleared in `finally` so that the next 401 wave
// (later token expiry) gets a fresh consent rather than reusing a
// stale promise.
const refreshInFlight = new Map<string, Promise<string>>();

export const refreshGoogleAccessToken = (scopes: string[]): Promise<string> => {
  const key = [...scopes].sort().join(" ");
  const existing = refreshInFlight.get(key);
  if (existing) return existing;
  const p = (async () => {
    await ensureGsiLoaded();
    return requestGoogleAccessToken(scopes);
  })().finally(() => {
    refreshInFlight.delete(key);
  });
  refreshInFlight.set(key, p);
  return p;
};
